<template>
  <b-dropdown class="template-dropdown" menu-class="shadow-sm overflow-visible" ref="dropdown" variant="link" :size="size" block no-caret :disabled="disabled">
    <template v-slot:button-content>
      <div class="custom-select text-left">
        <span v-if="!busy" style="display: inline-block; overflow: hidden; text-overflow: ellipsis; width: 95%;">
          {{ selected ? selected.name : placeholder }}
          <sup v-if="selected && selected.is_default">
            <b-badge variant="secondary" class="text-light">
              {{ trans('default') }}
            </b-badge>
          </sup>
        </span>
        <span v-if="busy">
          <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          {{ trans('please_wait') }}
        </span>
      </div>
    </template>
    <b-dropdown-form @submit.stop.prevent>
      <b-row>
        <b-col>
          <b-form-group class="mb-0">
            <b-form-input
              ref="search"
              type="search"
              v-model="search"
              size="sm"
              :placeholder="trans('search')"
              :debounce="500"
              :disabled="busy"
            />
          </b-form-group>
        </b-col>
        <b-col class="col-auto">
          <b-dropdown ref="filterType" variant="light" size="sm" class="filter-type" :disabled="busy">
            <template v-slot:button-content>
              <span :class="!filterType ? 'text-muted' : 'text-primary'">
                <i class="fa fa-fw fa-filter"></i>
                {{ trans(filterType || 'filter') }}
              </span>
            </template>
            <b-dropdown-item @click="filterType = null; onSearch()">
              <i :class="`fa fa-fw ${!filterType ? 'fa-check' : ''}`"></i>
              {{ trans('all') }}
            </b-dropdown-item>
            <b-dropdown-item @click="filterType = 'default'; onSearch()">
              <i :class="`fa fa-fw ${filterType == 'default' ? 'fa-check' : ''}`"></i>
              <span>{{ trans('default') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <b-col class="col-auto">
          <tags-filter
            ref="tagsFilter"
            :organization="organization"
            v-model="tag"
            type="template"
            :all-tags="all_tags"
            @all-tags="all_tags = $event"
            :disabled="busy"
          ></tags-filter>
        </b-col>
        <b-col class="col-auto">
          <b-button variant="light" size="sm" class="ml-2" @click="onRefresh" :disabled="busy">
            <i class="fa fa-fw fa-redo" v-if="!busy"></i>
            <i class="fa fa-fw fa-circle-notch fa-spin" v-if="busy"></i>
          </b-button>
        </b-col>
      </b-row>
    </b-dropdown-form>
    <b-dropdown-divider></b-dropdown-divider>
    <b-dropdown-text v-if="!!error" variant="danger">
      {{ error }}
    </b-dropdown-text>
    <b-dropdown-text>
      <b-overlay :show="busy">
        <b-table
          ref="table"
          :id="templatesTableId"
          class="bg-white table-row-click"
          small
          responsive
          borderless
          show-empty
          :fields="fields"
          :items="provider"
          :per-page="perPage"
          :current-page="currentPage"
          @row-clicked="onClickItem"
          thead-class="d-none"
        >
          <template #empty>
            <p v-if="busy">
              <i class="fa fa-fw fa-circle-notch fa-spin"></i>
              {{ trans('please_wait') }}
            </p>
            <p v-if="!busy" align="center">
              {{ trans('no_template_found') }}
            </p>
          </template>
          <template #cell(name)="data">
            <i :class="`fa fa-fw ${selected && selected.id == data.item.id ? 'fa-check' : ''}`"></i>
            {{ data.item.name }}
            <sup v-if="data.item.is_default">
              <b-badge variant="secondary" class="text-light">
                {{ trans('default') }}
              </b-badge>
            </sup>
            <tags
              :organization="organization"
              :model="data.item"
              inline
              type="template"
              hide-edit
            ></tags>
          </template>
        </b-table>
      </b-overlay>
    </b-dropdown-text>
    <b-dropdown-text>
      <b-row>
        <b-col>
          <b-pagination
            size="sm"
            :per-page="perPage"
            :total-rows="totalRows"
            v-model="currentPage"
            :aria-controls="templatesTableId"
            align="center"
          ></b-pagination>
        </b-col>
        <b-col v-if="nullable" class="col-auto">
          <b-button variant="link" size="sm" class="text-danger" @click="onClickClear" :disabled="!selected">
            <i class="fa fa-fw fa-times"></i>
            {{ trans('clear') }}
          </b-button>
        </b-col>
      </b-row>
    </b-dropdown-text>
  </b-dropdown>
</template>

<style>
  .template-dropdown.b-dropdown>button {
    padding: 0;
  }
</style>

<script>
  import Tags from './../tag/Tags'
  import TagsFilter from './../tag/TagsFilter'

  export default {
    props: {
      organization: Object,
      value: {
        type: Number,
        default: null,
      },
      size: {
        type: String,
        default: 'md',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      nullable: {
        type: Boolean,
        default: false,
      },
      placeholder: {
        type: String,
        default: trans('choose_a_template'),
      },
      default: {
        type: String,
      },
    },
    components: {
      Tags,
      TagsFilter,
    },
    data() {
      return {
        trans: trans,
        numeral: numeral,
        busy: false,
        error: null,
        items: null,
        fields: [
          {key: 'name', label: ''},
        ],
        selected: null,
        totalRows: null,
        currentPage: 1,
        lastPage: 1,
        perPage: 10,
        search: null,
        focusOnSearch: false,
        tag: null,
        all_tags: null,
        filterType: null,
        isNestedDropdownVisible: false,
      }
    },
    computed: {
      templatesTableId() {
        return 'templates' + this._uid
      },
    },
    watch: {
      search() {
        this.focusOnSearch = true
        this.onSearch()
      },
      tag() {
        this.onSearch()
      },
      value() {
        if (!this.value) {
          this.selected = this.value
        } else {
          this.selected = _.reduce(this.items, (selected, item) => {
            if (item.id == this.value) selected = item
            return selected
          }, null)
          if (!this.selected) this.onRefresh()
        }
      },
    },
   	methods: {
      onSearch() {
        this.currentPage = 1
        this.onRefresh()
      },
      onRefresh() {
        this.$refs.table.refresh()
      },
      onClickItem(item) {
        this.selected = item
        this.$refs.dropdown.hide(true)
        this.$emit('input', item.id)
      },
      onClickClear() {
        this.selected = null
        this.$refs.dropdown.hide(true)
        this.$emit('input', this.selected)
      },
   	  provider() {
        this.busy = true
        this.error = null
        let query = '?page=' + this.currentPage + '&per_page=' + this.perPage
        if (this.search) query += `&name=${encodeURIComponent(this.search)}`
        if (this.filterType) query += `&type=${encodeURIComponent(this.filterType)}`
        if (this.tag) query += `&tag=${encodeURIComponent(this.tag)}`
        if (this.default) query += `&default=${encodeURIComponent(this.default)}`
        let url = `/organization/${this.organization.id}/template/list${query}`
        if (this.value) url += `&selected=${this.value}`
        return axios.get(url)
        .then(response => {
          this.busy = false
          this.totalRows = response.data.total
          this.perPage = response.data.per_page
          this.currentPage = response.data.current_page
          this.lastPage = response.data.last_page
          this.items = response.data.data
          if (this.value) this.selected = response.data.selected ? response.data.selected : _.find(this.items, {id: this.value})
          if (!this.value && this.default) {
            this.selected = response.data.selected
            this.$emit('input', this.selected.id)
          }
          this.$emit('loaded')
          if (this.focusOnSearch) {
            this.focusOnSearch = false
            this.$nextTick(() => {
              this.$refs.search.focus()
            })
          }
          return this.items
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          this.items = []
          return this.items
        })
   	  },
   	},
   	mounted() {
      window['templateDropdown_'+this._uid] = this
      this.$root.$on('bv::dropdown::show', e => {
        if (this.$refs.filterType && e.target.parentNode === this.$refs.filterType.$el ||
          this.$refs.tagsFilter && e.target.parentNode === this.$refs.tagsFilter.$el)
          this.isNestedDropdownVisible = true
      })
      this.$root.$on('bv::dropdown::hide', e => {
        if (this.$refs.filterType && e.target.parentNode === this.$refs.filterType.$el ||
          this.$refs.tagsFilter && e.target.parentNode === this.$refs.tagsFilter.$el)
          this.isNestedDropdownVisible = false
        if (this.isNestedDropdownVisible) e.preventDefault()
      })
   	},
  }
</script>